import { Injectable, inject } from '@angular/core';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { HttpService } from './http.service';
import { SiteShiftConfigApiDto } from '../shared/models/drilling.models';

@Injectable({ providedIn: 'root' })
export class ShiftConfigService {
  private readonly http = inject(HttpService);
  private readonly shiftConfigCache = new Map<number, { expiresAt: number; stream: Observable<SiteShiftConfigApiDto> }>();
  private readonly shiftConfigTtlMs = 60 * 60 * 1000;

  getShiftConfig(siteId: number): Observable<SiteShiftConfigApiDto> {
    const cached = this.shiftConfigCache.get(siteId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.stream;
    }

    const request$ = this.http.get<SiteShiftConfigApiDto>(`Sites/${siteId}/shift-config`).pipe(
      tap({
        error: () => this.shiftConfigCache.delete(siteId),
      }),
      shareReplay(1),
    );

    this.shiftConfigCache.set(siteId, {
      expiresAt: Date.now() + this.shiftConfigTtlMs,
      stream: request$,
    });

    return request$;
  }

  putShiftConfig(siteId: number, dto: SiteShiftConfigApiDto): Observable<SiteShiftConfigApiDto> {
    return this.http.put<SiteShiftConfigApiDto, SiteShiftConfigApiDto>(`Sites/${siteId}/shift-config`, dto).pipe(
      tap(updatedDto => {
        this.shiftConfigCache.set(siteId, {
          expiresAt: Date.now() + this.shiftConfigTtlMs,
          stream: of(updatedDto).pipe(shareReplay(1)),
        });
      }),
    );
  }

  clearShiftConfigCache(siteId?: number): void {
    if (siteId == null) {
      this.shiftConfigCache.clear();
      return;
    }

    this.shiftConfigCache.delete(siteId);
  }
}
